import { BarChart } from '@mantine/charts';
import { Box, Container, Group, SimpleGrid, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract } from '@/api/generated/models';
import { useGetBoardManagementSummary } from '@/api/generated/board/board';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { IconFigmaAlertTriangle, IconFigmaCalendar, IconFigmaInfoNote } from '@/assets/icons';
import {
  CardSection,
  DateRangeField,
  ListPagination,
  MobileListFilters,
  SelectField
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import {
  buildPeriodOptions,
  formatBoardAlert,
  monthAbbrev,
  realizationRowColor
} from '@/features/board/boardDisplay';
import { AmountBasisPill } from '@/features/board/components/AmountBasisPill';
import { ContractRealizationTable } from '@/features/board/components/ContractRealizationTable';
import { ExecutiveKpiCard, KpiRow } from '@/features/board/components/ExecutiveKpiCard';
import { InfoBulletList, PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { currentLocale, formatAmount } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

const formatHours = (value: number) =>
  `${(value ?? 0).toLocaleString('hr-HR', { maximumFractionDigits: 0 })} h`;

/** KPI kartice: cijeli euri s grupiranjem tisuća (npr. "7.552.152 €"). */
const formatEuro = (value: number) => `${Math.round(value ?? 0).toLocaleString(currentLocale())} €`;

const formatPercent = (value: number) =>
  `${(value ?? 0).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

const MONTH_MIN_WIDTH = 90;

interface MonthMetaEntry {
  label: string;
  percentLabel: string;
  monthYear: string;
}

interface MonthTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  monthMeta: Record<string, MonthMetaEntry>;
}

/** Prilagođeni X-os tick — mjesec (medium, primarna boja) + postotak ispod (regular, muted), kao u Figmi. */
const MonthTick = ({ x, y, payload, monthMeta }: MonthTickProps) => {
  const meta = payload?.value ? monthMeta[payload.value] : undefined;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={12}
        textAnchor="middle"
        fontSize={10}
        fontWeight={500}
        fill="var(--color-text-primary)"
      >
        {meta?.label}
      </text>
      <text dy={26} textAnchor="middle" fontSize={10} fill="var(--color-text-muted)">
        {meta?.percentLabel}
      </text>
    </g>
  );
};

interface MonthlyTooltipPayloadEntry {
  dataKey?: string | number;
  value?: number;
}

interface MonthlyTooltipProps {
  active?: boolean;
  payload?: MonthlyTooltipPayloadEntry[];
  label?: string;
  monthMeta: Record<string, MonthMetaEntry>;
  invoicedKey: string;
  collectedKey: string;
}

/**
 * Custom tooltip sadržaj (zamjenjuje Mantine-ov default ChartTooltip) — korisnički zahtjev:
 * samo kvadratić + iznos za Fakturirano, pa ispod kvadratić + iznos za Naplaćeno, pa mjesec
 * MM/YYYY — bez labela uz kvadratiće (već postoje u legendi grafa iznad). Vrijednosti u payloadu
 * su u tisućama (isto kao os) — vraćamo ih na pune iznose za formatAmount.
 */
const MonthlyTooltip = ({
  active,
  payload,
  label,
  monthMeta,
  invoicedKey,
  collectedKey
}: MonthlyTooltipProps) => {
  if (!active || !payload?.length || !label) return null;
  const meta = monthMeta[label];
  const invoicedValue = payload.find((p) => p.dataKey === invoicedKey)?.value ?? 0;
  const collectedValue = payload.find((p) => p.dataKey === collectedKey)?.value ?? 0;
  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-soft)',
        borderRadius: 8,
        padding: '10px 12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
    >
      <Stack gap={4}>
        <Group gap={6} wrap="nowrap">
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: 'var(--chart-planned)',
              flexShrink: 0
            }}
          />
          <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {formatAmount(invoicedValue * 1000)}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap">
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: 'var(--chart-collected)',
              flexShrink: 0
            }}
          />
          <Text fz={12} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {formatAmount(collectedValue * 1000)}
          </Text>
        </Group>
        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
          {meta?.monthYear}
        </Text>
      </Stack>
    </div>
  );
};

export const BoardManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [period, setPeriod] = useState<string | null>('LAST_6_MONTHS');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);
  const [clientOrgId, setClientOrgId] = useState<string | null>(null);
  const [objectId, setObjectId] = useState<string | null>(null);
  // contractId se NE šalje na /board/management (taj endpoint filtrira samo po
  // objectId/clientOrgId) — Ugovor dropdown je prečac koji te dvije vrijednosti SAM postavlja
  // (vidi handleContractChange), stvarno filtriranje podataka i dalje ide preko objectId/clientOrgId.
  const [contractId, setContractId] = useState<string | null>(null);
  const isCustomPeriod = period === 'CUSTOM';

  const { data } = useGetBoardManagementSummary({
    period: period ?? undefined,
    fromDate: isCustomPeriod ? (dateRange[0] ?? undefined) : undefined,
    toDate: isCustomPeriod ? (dateRange[1] ?? undefined) : undefined,
    clientOrgId: clientOrgId ?? undefined,
    objectId: objectId ?? undefined,
    contractId: contractId ?? undefined
  });

  // Puna lista ugovora samo za unakrsno filtriranje tri dropdowna niže (Objekt/Ugovor/Klijent) —
  // svaki odabir sužava opcije preostala dva, a odabir Ugovora izravno postavlja Objekt i Klijent
  // (vidi handleContractChange).
  const { data: contractsData } = useListContracts();
  const allContracts = useMemo(
    () => ((contractsData as Contract[] | undefined) ?? []).filter((c) => c.id),
    [contractsData]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const clientOptions = useMemo(() => {
    const allowedClientIds = objectId
      ? new Set(allContracts.filter((c) => c.objectId === objectId).map((c) => c.clientOrgId))
      : null;
    return (connectionsData ?? [])
      .filter((c) => c.isActive)
      .map((c) => {
        const isOrgA = c.orgAId === user?.organisationId;
        return {
          value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
          label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
        };
      })
      .filter((option) => option.value)
      .filter((option) => !allowedClientIds || allowedClientIds.has(option.value));
  }, [connectionsData, allContracts, objectId, user?.organisationId]);

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(() => {
    const allowedObjectIds = clientOrgId
      ? new Set(allContracts.filter((c) => c.clientOrgId === clientOrgId).map((c) => c.objectId))
      : null;
    return (objectsData ?? [])
      .filter((object) => object.isActive && object.id)
      .filter((object) => !allowedObjectIds || allowedObjectIds.has(object.id))
      .map((object) => ({
        value: object.id as string,
        label: formatObjectLabel(object)
      }));
  }, [objectsData, allContracts, clientOrgId, t]);

  const contractOptions = useMemo(
    () =>
      allContracts
        .filter((c) => !objectId || c.objectId === objectId)
        .filter((c) => !clientOrgId || c.clientOrgId === clientOrgId)
        .map((c) => ({ value: c.id as string, label: c.contractNumber ?? c.name ?? '' })),
    [allContracts, objectId, clientOrgId]
  );

  // Objekt sužava i Ugovor i Klijent (opcije) — ako trenutno odabrani Ugovor/Klijent više nisu
  // valjani za novi objekt, briši ih umjesto da ostanu nevidljivi "duh" odabiri.
  const handleObjectChange = (value: string | null) => {
    setObjectId(value);
    if (!value) return;
    const selectedContract = allContracts.find((c) => c.id === contractId);
    if (selectedContract && selectedContract.objectId !== value) setContractId(null);
    if (
      clientOrgId &&
      !allContracts.some((c) => c.objectId === value && c.clientOrgId === clientOrgId)
    ) {
      setClientOrgId(null);
    }
  };

  // Klijent sužava i Ugovor i Objekt (opcije), simetrično handleObjectChange.
  const handleClientChange = (value: string | null) => {
    setClientOrgId(value);
    if (!value) return;
    const selectedContract = allContracts.find((c) => c.id === contractId);
    if (selectedContract && selectedContract.clientOrgId !== value) setContractId(null);
    if (objectId && !allContracts.some((c) => c.clientOrgId === value && c.objectId === objectId)) {
      setObjectId(null);
    }
  };

  // Ugovor POSTAVLJA (ne samo sužava) Objekt i Klijent na točne vrijednosti tog ugovora.
  const handleContractChange = (value: string | null) => {
    setContractId(value);
    if (!value) return;
    const selected = allContracts.find((c) => c.id === value);
    if (selected?.objectId) setObjectId(selected.objectId);
    if (selected?.clientOrgId) setClientOrgId(selected.clientOrgId);
  };

  // Prikaz u tisućama € (Figma: "0 / 20k / 40k / 60k") — čitljivije na mjesečnom grafu nego puni iznosi.
  // Backend (BoardDaoImpl.findMonthlyBilling) sad vraća JEDAN redak po mjesecu u cijelom odabranom
  // razdoblju (zero-filled) — svi mjeseci su uvijek prikazani, ne samo oni s fakturama. Kategorija
  // (dataKey) mora nositi i godinu da se dva "Tra" iz različitih godina (CUSTOM/"Sve" raspon > 12
  // mjeseci) ne poklope na grafu — prikazan tekst (samo mjesec) dolazi iz monthMeta preko MonthTick-a.
  const monthlyData = (data?.monthlyBilling ?? []).map((m) => ({
    monthKey: `${m.year}-${String(m.month).padStart(2, '0')}`,
    invoiced: m.invoiced ?? 0,
    collected: m.collected ?? 0,
    [t('board.chart.invoiced')]: (m.invoiced ?? 0) / 1000,
    [t('board.chart.collected')]: (m.collected ?? 0) / 1000
  }));
  const monthMeta = Object.fromEntries(
    (data?.monthlyBilling ?? []).map((m) => {
      const invoiced = m.invoiced ?? 0;
      const collected = m.collected ?? 0;
      return [
        `${m.year}-${String(m.month).padStart(2, '0')}`,
        {
          label: monthAbbrev(m.year ?? 0, m.month ?? 1),
          percentLabel: invoiced > 0 ? `${Math.round((collected / invoiced) * 100)} %` : '—',
          monthYear: `${String(m.month).padStart(2, '0')}/${m.year}`
        }
      ];
    })
  );

  // Backend već ograničava na najviše 7 redaka i (za partnera) radi GROUP BY/SUM u SQL-u — vidi
  // BoardService.getManagementSummary/BoardDaoImpl.findRealizationByPartner. Kad Partner filter
  // nije odabran, backend vraća partnerRealization (zbroj preko svih ugovora tog partnera); čim
  // je odabran, backend vraća praznu listu (svi redci bi već bili isti partner) i frontend
  // prikazuje contracts (po ugovoru) umjesto toga.
  // contracts se dijeli i s tablicom "Ugovori s odstupanjem" (treba PUNU listu), pa se ovdje samo
  // za graf dodatno siječe na istih 7 — plitka operacija nad već dohvaćenim nizom, bez nove upita.
  const MAX_CONTRACT_CHART_ROWS = 7;
  const contracts = data?.contracts ?? [];
  const realizationByContract = contracts
    .map((c) => ({
      key: c.contractId ?? c.contractNumber ?? '',
      label: c.contractNumber ?? '',
      percent:
        (c.plannedHours ?? 0) > 0
          ? Math.round(((c.realizedHours ?? 0) / (c.plannedHours ?? 1)) * 100)
          : 0
    }))
    .slice(0, MAX_CONTRACT_CHART_ROWS);

  const showRealizationByPartner = !clientOrgId;
  const realizationByPartner = (data?.partnerRealization ?? []).map((p) => ({
    key: p.clientOrgId ?? p.clientOrgName ?? '',
    label: p.clientOrgName ?? '',
    percent:
      (p.plannedHours ?? 0) > 0
        ? Math.round(((p.realizedHours ?? 0) / (p.plannedHours ?? 1)) * 100)
        : 0
  }));

  const realizationRows = showRealizationByPartner ? realizationByPartner : realizationByContract;

  const alerts = data?.alerts ?? [];
  const {
    page: alertsPage,
    setPage: setAlertsPage,
    pageSize: alertsPageSize,
    setPageSize: setAlertsPageSize,
    totalPages: alertsTotalPages,
    pageItems: pageAlerts
  } = useLocalPagination(alerts);
  // Promjena filtera može smanjiti broj upozorenja ispod trenutne stranice — vrati na zadnju postojeću.
  useEffect(() => {
    if (alertsPage > alertsTotalPages) setAlertsPage(alertsTotalPages);
  }, [alertsPage, alertsTotalPages, setAlertsPage]);
  const alertsPagination = (bordered: boolean) =>
    alerts.length > 0 && (
      <ListPagination
        label={t('board.alerts.showing', { shown: pageAlerts.length, total: alerts.length })}
        page={alertsPage}
        totalPages={alertsTotalPages}
        onPageChange={setAlertsPage}
        pageSize={alertsPageSize}
        onPageSizeChange={setAlertsPageSize}
        bordered={bordered}
      />
    );

  // KPI kartice (prijedlog s 5 KPI-jeva) — backend vraća sirove NETO zbrojeve (vidi
  // ManagementKpiTotals), izvedene vrijednosti (preostalo, postoci, čeka odobrenje, stopa naplate)
  // računaju se ovdje. Ugovoreni opseg / realizirano / odobreno su ukupni; fakturirano i naplaćeno
  // za fakture izdane u odabranom razdoblju.
  const kpi = data?.kpis;
  const contracted = kpi?.contractedValue ?? 0;
  const realized = kpi?.realizedValue ?? 0;
  const approved = kpi?.approvedValue ?? 0;
  const share = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0);
  const remaining = Math.max(0, contracted - realized);
  const realizedPercent = share(realized, contracted);
  const awaitingApproval = Math.max(0, realized - approved);
  const uninvoiced = kpi?.uninvoicedApproved ?? 0;
  const invoicedNet = kpi?.invoicedNet ?? 0;
  const collectedNet = kpi?.collectedNet ?? 0;
  const collectionRate = share(kpi?.collectedGross ?? 0, kpi?.invoicedGross ?? 0);

  const netSubtitle = t('board.kpi.netSubtitle');
  const ofContracted = (value: number) =>
    t('board.kpi.ofContracted', { percent: formatPercent(share(value, contracted)) });

  const kpiCards = [
    <ExecutiveKpiCard
      key="contracted"
      label={t('board.kpi.contracted')}
      info={t('board.kpi.contractedInfo')}
      value={formatEuro(contracted)}
      subtitle={netSubtitle}
      progress={contracted > 0 ? 100 : 0}
      progressCaption={t('board.kpi.cycleBase', { percent: formatPercent(100) })}
    >
      <KpiRow label={t('board.kpi.remaining')} value={formatEuro(remaining)} />
      <KpiRow
        label={t('board.kpi.remainingShare')}
        value={formatPercent(share(remaining, contracted))}
      />
    </ExecutiveKpiCard>,
    <ExecutiveKpiCard
      key="realized"
      label={t('board.kpi.realized')}
      info={t('board.kpi.realizedInfo')}
      value={formatEuro(realized)}
      subtitle={netSubtitle}
      progress={realizedPercent}
      progressCaption={ofContracted(realized)}
    >
      <KpiRow label={t('board.kpi.realizedHours')} value={formatHours(kpi?.realizedHours ?? 0)} />
      <KpiRow label={t('board.kpi.plannedHours')} value={formatHours(kpi?.plannedHours ?? 0)} />
    </ExecutiveKpiCard>,
    <ExecutiveKpiCard
      key="approved"
      label={t('board.kpi.approved')}
      info={t('board.kpi.approvedInfo')}
      value={formatEuro(approved)}
      subtitle={netSubtitle}
      progress={share(approved, contracted)}
      progressCaption={ofContracted(approved)}
    >
      <KpiRow label={t('board.kpi.awaitingApproval')} value={formatEuro(awaitingApproval)} />
      <KpiRow
        label={t('board.kpi.awaitingShare')}
        value={formatPercent(share(awaitingApproval, realized))}
      />
    </ExecutiveKpiCard>,
    <ExecutiveKpiCard
      key="invoiced"
      label={t('board.kpi.invoiced')}
      info={t('board.kpi.invoicedInfo')}
      value={formatEuro(invoicedNet)}
      subtitle={netSubtitle}
      progress={share(invoicedNet, contracted)}
      progressCaption={ofContracted(invoicedNet)}
      warning={uninvoiced > 0}
    >
      <KpiRow
        label={t('board.kpi.grossWithVat', { vat: formatEuro(kpi?.invoicedVat ?? 0) })}
        value={formatEuro(kpi?.invoicedGross ?? 0)}
      />
      <KpiRow label={t('board.kpi.uninvoiced')} value={formatEuro(uninvoiced)} />
    </ExecutiveKpiCard>,
    <ExecutiveKpiCard
      key="collected"
      label={t('board.kpi.collected')}
      info={t('board.kpi.collectedInfo')}
      value={formatEuro(collectedNet)}
      subtitle={netSubtitle}
      progress={share(collectedNet, contracted)}
      progressCaption={ofContracted(collectedNet)}
      warning={(kpi?.invoicedGross ?? 0) > 0 && collectionRate < 100}
    >
      <KpiRow
        label={t('board.kpi.grossWithVat', { vat: formatEuro(kpi?.collectedVat ?? 0) })}
        value={formatEuro(kpi?.collectedGross ?? 0)}
      />
      <KpiRow label={t('board.kpi.collectionRate')} value={formatPercent(collectionRate)} />
    </ExecutiveKpiCard>
  ];

  const monthlyBillingTitle = (titleKey: string) => (
    <Stack gap={6}>
      <Text fz={14} fw={600} lh="20px" style={{ color: 'var(--color-text-primary)' }}>
        {t(titleKey)}
      </Text>
      <AmountBasisPill label={t('board.chart.grossBadge')} />
    </Stack>
  );
  const monthlyBillingSwatches = (
    <Group gap={14} wrap="nowrap">
      <Group gap={6} wrap="nowrap">
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: 'var(--chart-planned)',
            flexShrink: 0
          }}
        />
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {t('board.chart.invoiced')}
        </Text>
      </Group>
      <Group gap={6} wrap="nowrap">
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: 'var(--chart-collected)',
            flexShrink: 0
          }}
        />
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {t('board.chart.collected')}
        </Text>
      </Group>
    </Group>
  );
  const monthlyBillingInfoIcon = (
    <PanelInfoIcon
      label={t('board.chart.monthlyBillingInfo')}
      title={t('board.chart.monthlyBillingTitle')}
    />
  );

  const monthlyBillingPanel = (
    <CardSection>
      <Box p={{ base: 16, sm: 20 }}>
        {/* Mobile — ikona uz naslov (ista pozicija kao svaki drugi panel), legenda u zasebnom
            retku ispod (naslov + legenda + ikona u jednom retku bi se stisnuli na uskom ekranu).
            Desktop — nepromijenjeno, sve u jednom retku. */}
        <Stack gap={12} mb={12} hiddenFrom="sm">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            {monthlyBillingTitle('board.chart.monthlyBillingTitleShort')}
            {monthlyBillingInfoIcon}
          </Group>
          {monthlyBillingSwatches}
        </Stack>
        <Group justify="space-between" align="center" mb={16} wrap="nowrap" visibleFrom="sm">
          {monthlyBillingTitle('board.chart.monthlyBillingTitle')}
          <Group gap={12} wrap="nowrap">
            {monthlyBillingSwatches}
            {monthlyBillingInfoIcon}
          </Group>
        </Group>
        {monthlyData.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('board.chart.empty')}
          </Text>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
            <div style={{ width: `max(100%, ${monthlyData.length * MONTH_MIN_WIDTH}px)` }}>
              <BarChart
                h={280}
                data={monthlyData}
                dataKey="monthKey"
                maxBarWidth={22}
                cursorFill="transparent"
                series={[
                  {
                    name: t('board.chart.invoiced'),
                    color: 'var(--chart-planned)'
                  },
                  { name: t('board.chart.collected'), color: 'var(--chart-collected)' }
                ]}
                withLegend={false}
                xAxisProps={{
                  height: 40,
                  tickLine: false,
                  tick: (props) => <MonthTick {...props} monthMeta={monthMeta} />
                }}
                tooltipProps={{
                  content: (props) => (
                    <MonthlyTooltip
                      {...props}
                      monthMeta={monthMeta}
                      invoicedKey={t('board.chart.invoiced')}
                      collectedKey={t('board.chart.collected')}
                    />
                  )
                }}
                valueFormatter={(value) => `${value.toLocaleString('hr-HR')}k €`}
              />
            </div>
          </div>
        )}
        <Group
          gap={10}
          wrap="nowrap"
          align="flex-start"
          mt={{ base: 12, sm: 16 }}
          px={12}
          py={10}
          style={{ borderRadius: 8, backgroundColor: 'var(--color-status-info-soft)' }}
        >
          <IconFigmaInfoNote color="var(--color-status-info)" />
          <Text fz={11} lh="16px" style={{ flex: 1, color: 'var(--color-text-muted)' }}>
            {t('board.chart.monthlyBillingCaption')}
          </Text>
        </Group>
      </Box>
    </CardSection>
  );

  const realizationByContractPanel = (
    <CardSection>
      <div style={{ padding: 20 }}>
        <Group justify="space-between" align="center" mb={16} wrap="nowrap">
          <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
            {t(
              showRealizationByPartner
                ? 'board.chart.realizationByPartnerTitle'
                : 'board.chart.realizationByContractTitle'
            )}
          </Text>
          <PanelInfoIcon
            label={t(
              showRealizationByPartner
                ? 'board.chart.realizationByPartnerInfo'
                : 'board.chart.realizationByContractInfo'
            )}
          />
        </Group>
        {realizationRows.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('board.chart.empty')}
          </Text>
        ) : (
          <Stack gap={16}>
            {realizationRows.map((row) => (
              <Group key={row.key} gap={12} wrap="nowrap" align="center">
                <Tooltip
                  label={row.label}
                  openDelay={300}
                  disabled={!row.label}
                  color="var(--color-bg-surface)"
                  styles={{
                    tooltip: {
                      color: 'var(--color-text-primary)',
                      border: '1px solid var(--color-border-soft)',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <Text
                    fz={13}
                    truncate="end"
                    data-own-tooltip
                    style={{
                      color: 'var(--color-text-secondary)',
                      width: 96,
                      flexShrink: 0
                    }}
                  >
                    {row.label}
                  </Text>
                </Tooltip>
                <div style={{ flex: 1, height: 14, minWidth: 1, position: 'relative' }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${Math.min(Math.max(row.percent, 0), 100)}%`,
                      backgroundColor: realizationRowColor(row.percent),
                      borderTopRightRadius: 4,
                      borderBottomRightRadius: 4
                    }}
                  />
                </div>
                <Text
                  fz={13}
                  style={{
                    color: 'var(--color-text-primary)',
                    width: 56,
                    flexShrink: 0,
                    textAlign: 'right'
                  }}
                >
                  {row.percent} %
                </Text>
              </Group>
            ))}
          </Stack>
        )}
        <Text fz={12} mt={12} style={{ color: 'var(--color-text-muted)' }}>
          {t('board.chart.realizationByContractCaption')}
        </Text>
      </div>
    </CardSection>
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        <Stack gap={4}>
          <Group gap={10} align="center">
            <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
              {t('board.title')}
            </Title>
            <Box
              px={8}
              py={4}
              style={{ borderRadius: 999, backgroundColor: 'var(--color-status-warning-soft)' }}
            >
              <Text
                fz={11}
                fw={600}
                lh="14px"
                style={{ whiteSpace: 'nowrap', color: 'var(--color-status-warning)' }}
              >
                {t('board.managementBadge')}
              </Text>
            </Box>
          </Group>
          <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
            {t('board.managementSubtitle')}
          </Text>
        </Stack>

        {/* Mobile — isti obrazac kao liste/nadzorna ploča: sažetak + ikona filtera, sheet s filterima. */}
        <Box hiddenFrom="sm" w="100%">
          <MobileListFilters
            filters={[
              {
                key: 'period',
                label: t('board.filterPeriod'),
                options: buildPeriodOptions(),
                value: period,
                onChange: (value) => setPeriod(value ?? 'LAST_6_MONTHS'),
                emptyValue: 'LAST_6_MONTHS',
                variant: 'buttons',
                dateRange: {
                  showWhen: 'CUSTOM',
                  label: t('board.filterDateRange'),
                  value: dateRange,
                  onChange: setDateRange
                }
              },
              {
                key: 'object',
                label: t('board.filterObject'),
                options: objectOptions,
                value: objectId,
                onChange: setObjectId
              },
              {
                key: 'contract',
                label: t('board.filterContract'),
                options: contractOptions,
                value: contractId,
                onChange: setContractId
              },
              {
                key: 'client',
                label: t('board.filterClient'),
                options: clientOptions,
                value: clientOrgId,
                onChange: setClientOrgId
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <div style={{ width: 200 }}>
            <SelectField
              label={t('board.filterPeriod')}
              data={buildPeriodOptions()}
              value={period}
              onChange={(value) => {
                setPeriod(value);
                if (value !== 'CUSTOM') setDateRange([null, null]);
              }}
              allowDeselect={false}
              leftSection={<IconFigmaCalendar size={16} />}
            />
          </div>
          {isCustomPeriod && (
            <div style={{ width: 260 }}>
              <DateRangeField
                label={t('board.filterDateRange')}
                placeholder={t('board.filterDateRange')}
                hideCalendarIcon
                valueFormat="DD.MM.YYYY."
                value={dateRange}
                onChange={setDateRange}
                clearable
              />
            </div>
          )}
          <div style={{ width: 220 }}>
            <SelectField
              label={t('board.filterObject')}
              placeholder={t('board.filterObjectPlaceholder')}
              data={objectOptions}
              value={objectId}
              onChange={handleObjectChange}
              clearable
            />
          </div>
          <div style={{ width: 220 }}>
            <SelectField
              label={t('board.filterContract')}
              placeholder={t('board.filterContractPlaceholder')}
              data={contractOptions}
              value={contractId}
              onChange={handleContractChange}
              clearable
            />
          </div>
          <div style={{ width: 220 }}>
            <SelectField
              label={t('board.filterClient')}
              placeholder={t('board.filterClientPlaceholder')}
              data={clientOptions}
              value={clientOrgId}
              onChange={handleClientChange}
              clearable
            />
          </div>
        </Group>

        {/* 5 KPI kartica: 1 stupac na mobitelu, 2 na tabletu, 5 u redu na širokom ekranu. */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }} spacing={16}>
          {kpiCards}
        </SimpleGrid>

        {/* Mobile (Figma BD-02M) — oba panela puna širina, naslagana (ne 7fr/4fr u istom retku,
            premalo mjesta za čitljiv graf). Isti JSX elementi kao desktop ispod. */}
        <Stack gap={16} hiddenFrom="sm" w="100%">
          {monthlyBillingPanel}
          {realizationByContractPanel}
        </Stack>

        {/*
          Omjer 7fr/4fr (≈63/37) oponaša Figma "Charts Row" (712px / 400px ≈ 63/37), ne 50/50.
          CSS grid (gap=16, isti kao ostatak stranice) — fr jedinice uvijek popune TOČNO 100%
          širine (bez "rupe" koju je flex-grow + minWidth kombinacija ostavljala), a grid djeca se
          po defaultu rastežu i po visini (iste visine panela bez dodatnih wrapper diveva).
        */}
        <Box visibleFrom="sm" w="100%">
          <div style={{ display: 'grid', gridTemplateColumns: '7fr 4fr', gap: 16 }}>
            {monthlyBillingPanel}
            {realizationByContractPanel}
          </div>
        </Box>

        <ContractRealizationTable
          title={t('board.table.managementTitle')}
          info={t('board.table.managementInfo')}
          rows={contracts}
        />

        {/* Mobile (Figma BD-02M) — bez okvira panela, samo naslov + info ikona pa lista (točka +
            tekst, bez razdjelnika između redaka). Desktop ostaje nepromijenjen ispod. */}
        <Stack gap={12} hiddenFrom="sm" w="100%">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {t('board.alerts.title')}
            </Text>
            <PanelInfoIcon
              width={300}
              title={t('board.alerts.title')}
              label={
                <InfoBulletList
                  intro={t('board.alerts.infoIntro')}
                  items={[
                    t('board.alerts.infoBelowPlan'),
                    t('board.alerts.infoNotStarted'),
                    t('board.alerts.infoClientLate'),
                    t('board.alerts.infoClientOverdue'),
                    t('board.alerts.infoWeekReportWaiting')
                  ]}
                />
              }
            />
          </Group>
          {alerts.length === 0 ? (
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('board.alerts.empty')}
            </Text>
          ) : (
            pageAlerts.map((alert, index) => (
              <Group
                key={`${alert.type}-${alert.contractId ?? alert.clientOrgId ?? index}`}
                gap={8}
                wrap="nowrap"
                align="center"
                role={alert.contractId ? 'button' : undefined}
                tabIndex={alert.contractId ? 0 : undefined}
                onClick={() => alert.contractId && navigate(`/contracts/${alert.contractId}`)}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && alert.contractId)
                    navigate(`/contracts/${alert.contractId}`);
                }}
                style={{ cursor: alert.contractId ? 'pointer' : undefined }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 999,
                    flexShrink: 0,
                    backgroundColor:
                      alert.type === 'WEEK_REPORT_WAITING_ON_CLIENT'
                        ? 'var(--color-status-info)'
                        : 'var(--color-status-danger)'
                  }}
                />
                <Text fz={13} style={{ color: 'var(--color-text-secondary)', flex: 1 }}>
                  {formatBoardAlert(t, alert)}
                </Text>
              </Group>
            ))
          )}
          {alertsPagination(false)}
        </Stack>

        <Box visibleFrom="sm">
          <CardSection
            title={t('board.alerts.title')}
            headerRight={
              <PanelInfoIcon
                width={300}
                title={t('board.alerts.title')}
                label={
                  <InfoBulletList
                    intro={t('board.alerts.infoIntro')}
                    items={[
                      t('board.alerts.infoBelowPlan'),
                      t('board.alerts.infoNotStarted'),
                      t('board.alerts.infoClientLate'),
                      t('board.alerts.infoClientOverdue'),
                      t('board.alerts.infoWeekReportWaiting')
                    ]}
                  />
                }
              />
            }
          >
            {alerts.length === 0 ? (
              <Text fz={13} p={20} style={{ color: 'var(--color-text-muted)' }}>
                {t('board.alerts.empty')}
              </Text>
            ) : (
              <Stack gap={0}>
                {pageAlerts.map((alert, index) => (
                  <Group
                    key={`${alert.type}-${alert.contractId ?? alert.clientOrgId ?? index}`}
                    gap={10}
                    wrap="nowrap"
                    px={20}
                    py={12}
                    role={alert.contractId ? 'button' : undefined}
                    tabIndex={alert.contractId ? 0 : undefined}
                    onClick={() => alert.contractId && navigate(`/contracts/${alert.contractId}`)}
                    onKeyDown={(event) => {
                      if ((event.key === 'Enter' || event.key === ' ') && alert.contractId)
                        navigate(`/contracts/${alert.contractId}`);
                    }}
                    style={{
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                      cursor: alert.contractId ? 'pointer' : undefined
                    }}
                  >
                    <div
                      style={{
                        color: 'var(--color-status-warning)',
                        display: 'flex',
                        flexShrink: 0
                      }}
                    >
                      <IconFigmaAlertTriangle size={16} />
                    </div>
                    <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                      {formatBoardAlert(t, alert)}
                    </Text>
                  </Group>
                ))}
              </Stack>
            )}
            {alertsPagination(true)}
          </CardSection>
        </Box>
      </Stack>
    </Container>
  );
};
